import React, { useMemo } from 'react'
import { motion } from 'framer-motion'
import { StatsCard } from './stats/StatsCard'
import tagIcon from '@/assets/dashboard/tag.svg'
import { formatMoney } from '@/utils'

export interface AverageAmountBarData {
  value: number // Value for the bar height (0-100 or actual value)
}

interface AverageAmountChartProps {
  data: AverageAmountBarData[]
  className?: string
  color?: string // Color for bars (default: gray)
}

const AverageAmountChart: React.FC<AverageAmountChartProps> = ({ 
  data,
  className = '',
  color = '#b1b0b0'
}) => {
  // Calculate normalized heights based on max value
  const normalizedData = useMemo(() => {
    const maxValue = Math.max(...data.map(d => d.value), 1)
    return data.map(item => ({
      ...item,
      height: (item.value / maxValue) * 50 // Max height is 50px
    }))
  }, [data])

  // Calculate average position
  const avgHeight = useMemo(() => {
    const avgValue = normalizedData.reduce((sum, item) => sum + item.height, 0) / normalizedData.length
    return avgValue
  }, [normalizedData])

  const barWidth = 214 / normalizedData.length

  const grey = {
    borderTop: '#cccdc5',
    from: 'rgba(197,204,159,0.32)',
    to: 'rgba(255,255,255,0)',
  }
  const lime = {
    borderTop: '#c8ed03',
    from: 'rgba(200,237,3,0.3)',
    to: 'rgba(200,237,3,0)',
  }

  return (
    <div className={`relative w-full max-w-[214px] h-[80px] ${className}`}>
      {/* Average line */}
      <div 
        className="absolute left-0 w-full h-px bg-[rgba(45,41,35,0.72)] dark:bg-[rgba(253,253,253,0.72)] z-10"
        style={{
          top: `${60 - avgHeight}px`
        }}
      />

      {/* Avg label */}
      <div 
        className="absolute left-0 flex items-center justify-center py-1 px-1 bg-[#2d2923] dark:bg-[#fdfdfd] z-10"
        style={{
          top: `${60 - avgHeight - 8}px`
        }}
      >
        <p className="text-center text-white dark:text-[#0a0a0a] text-[11px] leading-3 font-semibold">
          Avg
        </p>
      </div>

      {/* Bars */}
      {normalizedData.map((item, index) => {
        const leftPosition = index * barWidth
        const barWidthRatio = 0.5
        const barPixelWidth = barWidth * barWidthRatio
        const centeredLeft = leftPosition + (barWidth - barPixelWidth) / 2
        
        const palette = grey
        
        return (
          <div
            key={`bar-${index}`}
            className="absolute bottom-0"
            style={{
              left: `${centeredLeft}px`,
              width: `${barPixelWidth}px`,
            }}
          >
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: item.height, opacity: 1 }}
              transition={{
                duration: 0.6,
                delay: index * 0.05,
                ease: [0.16, 1, 0.3, 1]
              }}
              className="w-full rounded-t-full"
              style={{
                background: `linear-gradient(to bottom, ${palette.from} 0%, ${palette.to} 100%)`,
                height: `${item.height}px`,
              }}
            />
          </div>
        )
      })}
    </div>
  )
}

interface AverageAmountCardProps {
  averageAmount: number
  loading?: boolean
}

// Deterministic PRNG so bars depend on real values but don't "jump" on rerender.
function mulberry32(seed: number) {
  let t = seed >>> 0
  return () => {
    t += 0x6d2b79f5
    let r = Math.imul(t ^ (t >>> 15), 1 | t)
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r)
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296
  }
}

/**
 * Screen: OTCScreen
 * Average Amount card extracted as a standalone component.
 */
export const AverageAmountCard: React.FC<AverageAmountCardProps> = ({ averageAmount, loading = false }) => {
  const chartData = useMemo<AverageAmountBarData[]>(() => {
    // 14 bars: 8 grey + 6 lime (Figma 37:1124 structure)
    const rng = mulberry32(Math.max(1, Math.floor(averageAmount)))
    return Array.from({ length: 15 }, () => ({
      value: 10 + Math.round(rng() * 90),
    }))
  }, [averageAmount])

  return (
    <StatsCard
      icon={tagIcon}
      iconAlt="Average Amount"
      label="Average Amount"
      value={
        <>
          <span className="text-foreground/48">$</span>
          <span className="text-foreground">{formatMoney(averageAmount).replace('$', '').trim()}</span>
        </>
      }
      loading={loading}
      chart={
        <AverageAmountChart data={chartData} />
      }
    />
  )
}
